(() => {
  if (customElements.get('bn-plaque-comparison')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const AUTO_ADVANCE_MS = 4000;

  class BnPlaqueComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.dataset.enhanced = 'true';
      this.cleanups = [];

      const mobileMedia = window.matchMedia(MOBILE_QUERY);
      const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

      this.querySelectorAll('.bn-pci__panel').forEach((panel) => {
        const stage = panel.querySelector('.bn-pci__mobile-stage');
        const canvas = panel.querySelector('.bn-pci__canvas');
        const canvasImage = canvas?.querySelector('img');
        const overlayCards = [...panel.querySelectorAll('.bn-pci__overlay-card')];
        const mobileCards = [...panel.querySelectorAll('.bn-pci__mobile-card')];
        const mobileButtons = [...panel.querySelectorAll('.bn-pci__mobile-card-button')];
        const mobileCallouts = panel.querySelector('.bn-pci__mobile-callouts');
        const ids = overlayCards.map((card) => card.dataset.callout).filter(Boolean);
        const leaders = [...panel.querySelectorAll('.bn-pci__leader')];
        const markers = [...panel.querySelectorAll('.bn-pci__leader, .bn-pci__hotspot')];
        const mobileConnector = panel.querySelector('.bn-pci__mobile-connector');
        const connectorPath = mobileConnector?.querySelector('path');
        const connectorTarget = mobileConnector?.querySelector('circle');
        const controller = new AbortController();
        const { signal } = controller;
        let timer = null;
        let routeFrame = null;
        let pausedByShopper = false;

        if (!ids.length) return;

        const activeIndex = () => Math.max(0, ids.indexOf(panel.dataset.activeCallout));

        const syncLeaderStarts = () => {
          leaders.forEach((leader) => {
            const line = leader.querySelector('line');
            if (!line) return;
            line.setAttribute('x1', leader.dataset.desktopX);
            line.setAttribute('y1', leader.dataset.desktopY);
          });
        };

        const syncMobileInteractivity = () => {
          mobileButtons.forEach((button) => {
            button.tabIndex = mobileMedia.matches ? 0 : -1;
          });
        };

        const routeMobileConnector = () => {
          routeFrame = null;
          if (!mobileMedia.matches || !stage || !canvas || !mobileConnector || !connectorPath || !connectorTarget) return;

          const id = panel.dataset.activeCallout || ids[0];
          const leader = leaders.find((item) => item.dataset.callout === id);
          const button = mobileButtons.find((item) => item.dataset.callout === id);
          if (!leader || !button) return;

          const stageBox = stage.getBoundingClientRect();
          const canvasBox = canvas.getBoundingClientRect();
          const buttonBox = button.getBoundingClientRect();
          if (!stageBox.width || !stageBox.height || !canvasBox.width || !canvasBox.height || !buttonBox.width) return;

          const targetPercentX = Number.parseFloat(leader.dataset.targetX || '0');
          const targetPercentY = Number.parseFloat(leader.dataset.targetY || '0');
          const targetX = canvasBox.left - stageBox.left + (canvasBox.width * targetPercentX / 100);
          const targetY = canvasBox.top - stageBox.top + (canvasBox.height * targetPercentY / 100);
          const cardX = buttonBox.left - stageBox.left + (buttonBox.width / 2);
          const cardY = buttonBox.top - stageBox.top;
          const canvasBottom = canvasBox.bottom - stageBox.top;
          const turnY = canvasBottom + Math.max(5, Math.min(10, (cardY - canvasBottom) / 2));
          const buttonIndex = mobileButtons.indexOf(button);
          const isSecondRow = buttonIndex >= 2;
          const width = Math.max(1, Math.round(stageBox.width));
          const height = Math.max(1, Math.round(stageBox.height));
          let route = `M ${targetX} ${targetY} L ${targetX} ${turnY} L ${cardX} ${turnY} L ${cardX} ${cardY}`;

          if (isSecondRow && mobileCallouts) {
            const calloutsBox = mobileCallouts.getBoundingClientRect();
            const channelX = calloutsBox.left - stageBox.left + (calloutsBox.width / 2);
            const entryX = buttonIndex % 2 === 0
              ? buttonBox.right - stageBox.left
              : buttonBox.left - stageBox.left;
            const entryY = buttonBox.top - stageBox.top + Math.min(38, buttonBox.height * 0.28);
            route = `M ${targetX} ${targetY} L ${targetX} ${turnY} L ${channelX} ${turnY} L ${channelX} ${entryY} L ${entryX} ${entryY}`;
          }

          mobileConnector.setAttribute('viewBox', `0 0 ${width} ${height}`);
          mobileConnector.setAttribute('width', String(width));
          mobileConnector.setAttribute('height', String(height));
          mobileConnector.dataset.callout = id;
          mobileConnector.dataset.targetX = String(targetPercentX);
          mobileConnector.dataset.targetY = String(targetPercentY);
          connectorPath.setAttribute('d', route);
          connectorTarget.setAttribute('cx', String(targetX));
          connectorTarget.setAttribute('cy', String(targetY));
        };

        const requestConnectorRoute = () => {
          if (routeFrame !== null) window.cancelAnimationFrame(routeFrame);
          routeFrame = window.requestAnimationFrame(routeMobileConnector);
        };

        const activate = (id) => {
          if (!ids.includes(id)) return;

          panel.dataset.activeCallout = id;
          overlayCards.forEach((card) => {
            const selected = card.dataset.callout === id;
            card.setAttribute('aria-pressed', String(selected));
            card.classList.toggle('is-active', selected);
          });
          mobileCards.forEach((card) => {
            card.classList.toggle('is-active', card.dataset.callout === id);
            card.removeAttribute('aria-hidden');
          });
          mobileButtons.forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.callout === id));
          });
          markers.forEach((marker) => {
            marker.classList.toggle('is-active', marker.dataset.callout === id);
          });
          requestConnectorRoute();
        };

        const stopTimer = () => {
          if (timer !== null) window.clearTimeout(timer);
          timer = null;
        };

        const scheduleTimer = () => {
          stopTimer();
          if (!mobileMedia.matches || reducedMotionMedia.matches || pausedByShopper) return;
          timer = window.setTimeout(() => {
            activate(ids[(activeIndex() + 1) % ids.length]);
            scheduleTimer();
          }, AUTO_ADVANCE_MS);
        };

        const pauseByShopper = () => {
          pausedByShopper = true;
          panel.dataset.autoPaused = 'true';
          stopTimer();
        };

        const selectByShopper = (id) => {
          pauseByShopper();
          activate(id);
        };

        overlayCards.forEach((card) => {
          const select = () => selectByShopper(card.dataset.callout);
          card.addEventListener('pointerenter', select, { signal });
          card.addEventListener('focus', select, { signal });
          card.addEventListener('click', select, { signal });
        });
        mobileButtons.forEach((button) => {
          button.addEventListener('focus', () => selectByShopper(button.dataset.callout), { signal });
          button.addEventListener('click', () => selectByShopper(button.dataset.callout), { signal });
        });
        panel.addEventListener('pointerdown', pauseByShopper, { signal });

        const handleMediaChange = () => {
          syncLeaderStarts();
          syncMobileInteractivity();
          activate(panel.dataset.activeCallout || ids[0]);
          scheduleTimer();
        };
        mobileMedia.addEventListener('change', handleMediaChange);
        reducedMotionMedia.addEventListener('change', handleMediaChange);

        const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(requestConnectorRoute) : null;
        if (stage) resizeObserver?.observe(stage);
        canvasImage?.addEventListener('load', requestConnectorRoute, { signal });

        syncLeaderStarts();
        syncMobileInteractivity();
        activate(ids[0]);
        scheduleTimer();

        this.cleanups.push(() => {
          stopTimer();
          if (routeFrame !== null) window.cancelAnimationFrame(routeFrame);
          resizeObserver?.disconnect();
          controller.abort();
          mobileMedia.removeEventListener('change', handleMediaChange);
          reducedMotionMedia.removeEventListener('change', handleMediaChange);
        });
      });
    }

    disconnectedCallback() {
      this.cleanups?.forEach((cleanup) => cleanup());
      this.cleanups = [];
      delete this.dataset.bound;
    }
  }

  customElements.define('bn-plaque-comparison', BnPlaqueComparison);
})();
